import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { Home, MapPin, Percent, Plus, Store } from "lucide-react";
import { Badge, Button, EmptyState, buttonVariants } from "@/components/ui";
import { ListResultsCard } from "@/components/common";
import type { House } from "@/types";
import { commissionPercentStringFromRate } from "@/lib/commissionRate";

interface AdminUserStructuresSectionProps {
  hostUserId: string;
  houses: House[];
  isLoading: boolean;
  title: string;
  emptyMessage: string;
  canCreateActivity: boolean;
  canCreateHouse: boolean;
}

export function AdminUserStructuresSection({
  hostUserId,
  houses,
  isLoading,
  title,
  emptyMessage,
  canCreateActivity,
  canCreateHouse,
}: AdminUserStructuresSectionProps) {
  const { t } = useTranslation("adminUsers");

  return (
    <ListResultsCard
      title={title}
      headerAction={
        <div className="flex flex-wrap gap-2">
          {canCreateActivity ? (
            <Link
              to={`/admin/create-activity?owner_user_id=${hostUserId}&context=host`}
            >
              <Button
                type="button"
                variant="outline"
                size="sm"
                leftIcon={<Store className="h-4 w-4" />}
              >
                {t("detail.addActivity")}
              </Button>
            </Link>
          ) : null}
          {canCreateHouse ? (
            <Link to={`/admin/create-house?host_user_id=${hostUserId}`}>
              <Button
                type="button"
                size="sm"
                leftIcon={<Plus className="h-4 w-4" />}
              >
                {t("detail.addHouse")}
              </Button>
            </Link>
          ) : null}
        </div>
      }
      contentClassName="p-0"
      isLoading={isLoading}
      isEmpty={!isLoading && houses.length === 0}
      emptyState={
        <div className="p-5">
          <EmptyState
            size="md"
            icon={Home}
            title={emptyMessage}
            action={
              canCreateHouse ? (
                <Link
                  to={`/admin/create-house?host_user_id=${hostUserId}`}
                  className={buttonVariants({ variant: "outline", size: "sm" })}
                >
                  <Plus
                    className="mr-1.5 h-4 w-4"
                    aria-hidden
                  />
                  {t("detail.addHouse")}
                </Link>
              ) : undefined
            }
          />
        </div>
      }
    >
      <div className="divide-surface-100 divide-y">
        {houses.map((house) => (
          <Link
            key={house.id}
            to={`/houses/${house.id}`}
            className="hover:bg-surface-50 dark:hover:bg-surface-100/50 flex items-center justify-between gap-4 px-5 py-3 transition-colors"
          >
            <div className="flex min-w-0 items-center gap-3">
              <span className="bg-success-50 text-success-600 ring-success-600/15 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset">
                <Home
                  className="h-4 w-4"
                  aria-hidden
                />
              </span>
              <div className="min-w-0">
                <div className="text-surface-900 truncate text-sm font-medium">
                  {house.name}
                </div>
                <div className="text-surface-500 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[13px]">
                  {house.address ? (
                    <span className="flex min-w-0 items-center gap-1">
                      <MapPin className="text-surface-400 h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{house.address}</span>
                    </span>
                  ) : null}
                  <span className="flex items-center gap-1 tabular-nums">
                    <Percent className="text-surface-400 h-3.5 w-3.5" />
                    {t("detail.houseRowCommission", {
                      percent: commissionPercentStringFromRate(
                        house.commission_rate,
                      ),
                    })}
                  </span>
                </div>
              </div>
            </div>
            <Badge
              variant={house.is_active ? "success" : "default"}
              size="sm"
              dot
            >
              {house.is_active ? t("detail.active") : t("detail.inactive")}
            </Badge>
          </Link>
        ))}
      </div>
    </ListResultsCard>
  );
}
